import { useState, useCallback } from 'react';

interface StorageConfig<T> {
  key: string;
  initialValue?: T;
  queryValue?: string; // Assumed to be string if present
}

/**
 * Custom hook for persisting calculator values in localStorage
 * and initializing from either localStorage or URL query parameters
 */
export function useCalculatorStorage<T = string>({
  key,
  initialValue = '' as unknown as T,
  queryValue,
}: StorageConfig<T>): [T, (newValue: T) => void] {
  const [internalValue, setInternalValue] = useState<T>(() => {
    // Initializer Priority: queryValue > localStorage > initialValue
    if (queryValue && queryValue.trim() !== '') {
      return queryValue as unknown as T;
    }
    const savedValue = localStorage.getItem(key);
    // If savedValue is a string (even an empty one), use it.
    // If savedValue is null (key not found by spec) or undefined (non-standard but possible in tests), fall back.
    if (typeof savedValue === 'string') {
      return savedValue as unknown as T;
    }
    return initialValue;
  });

  // Custom setter that updates state and persists to localStorage.
  // This is what components will call (e.g., for onClick handlers).
  const setValue = useCallback(
    (newValue: T) => {
      setInternalValue(newValue);
      if (newValue !== undefined) {
        localStorage.setItem(key, newValue as string);
      } else {
        localStorage.removeItem(key);
      }
    },
    [key]
  );

  return [internalValue, setValue];
}
